import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, damp, legIK, boneG } from './kit';
/* ============================================================
   SICHEL 鐮 —— BOSS・旋掃
   ============================================================

   剪影：雙足＋9.6 單位橫旋刃
   二階段：外刃甩脫・轉速三倍並開砲（紅→橙）

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-04 SICHEL 鐮 ── BAR 家族推到極限：一把 9.6 單位寬的旋刃。
   P1 慢速橫掃（紅・接觸）→ 跳或退。
   P2 外刃甩脫，轉速三倍並張開砲（橙）→ 逼你進到刃內側。 */
export function buildSichel(){
  var GL = glowMat(TINT.red.hex, 1.9), GLA = glowMat(TINT.amb.hex, 2.1);
  var root = new THREE.Group(), i, s;
  var hips = boneG(root, 0, 3.10, 0);
  hips.add(bevel(M.dark,  1.90, 1.30, 1.70, [0,0,0]));
  hips.add(plate(M.armor, 2.06, 0.56, 0.90, [0, 0.40, 0.52], -0.32));
  hips.add(plate(M.armor, 1.96, 0.50, 0.80, [0, 0.36,-0.56],  0.28));
  hips.add(rib  (M.armor, 2.10, 0.10, 0.10, [0, 0.62, 0.28], -0.32));
  var legs = [];
  for (i = 0; i < 2; i++){
    s = i ? -1 : 1;
    var th = boneG(hips, s*0.86, -0.30, 0);
    th.add(mesh(G.cyl, M.armor, [0.62,1.60,0.62], [0,-0.80,0]));
    th.add(mesh(G.cyl, M.trim,  [0.70,0.30,0.70], [0,-1.56,0], [0,0,HP]));
    var sh = boneG(th, 0, -1.60, 0);
    sh.add(mesh(G.cyl, M.armor, [0.50,1.52,0.50], [0,-0.76,0]));
    var kn = mesh(G.oct, GL, [0.40,0.40,0.40], [0, 0.02, -0.16]);
    kn.userData.noMerge = 1; sh.add(kn);
    var ft = boneG(sh, 0, -1.52, 0);
    ft.add(mesh(G.box, M.dark, [0.78,0.30,1.60], [0,-0.14,0.20]));
    legs.push({th:th, sh:sh, ft:ft, kn:kn, side:s});
  }
  /* 旋刃：外刃是可破壞件，P2 直接不見 —— 剪影會變，玩家立刻知道規則換了 */
  var rot = boneG(hips, 0, 0.92, 0);
  rot.add(mesh(G.cyl, M.dark,  [1.30,0.62,1.30], [0,0,0]));
  rot.add(mesh(G.tor, M.trim,  [2.00,2.00,0.42], [0,0,0], [HP,0,0]));
  var tips = [];
  for (i = 0; i < 2; i++){
    s = i ? -1 : 1;
    rot.add(mesh(G.box, M.armor, [4.60,0.26,0.86], [s*2.60,0.06,0]));
    rot.add(mesh(G.box, M.armor, [0.80,0.24,3.20], [0,-0.02,s*1.80]));
    var tp = new THREE.Group(); tp.position.set(s*4.72, 0.06, 0); rot.add(tp);
    tp.add(mesh(G.cone, M.armor, [0.62,1.10,0.62], [0,0,0], [0,0,-s*HP]));
    var tg = mesh(G.box, GL, [0.90,0.10,0.14], [s*-0.40,0.10,0]);
    tg.userData.noMerge = 1; tp.add(tg);
    var tp2 = new THREE.Group(); tp2.position.set(0,-0.02,s*3.10); rot.add(tp2);
    tp2.add(mesh(G.cone, M.armor, [0.52,0.90,0.52], [0,0,0], [s*HP,0,0]));
    tips.push(tp, tp2);
  }
  var guns = [];
  for (i = 0; i < 2; i++){
    s = i ? -1 : 1;
    var gp = new THREE.Group(); gp.position.set(s*1.06, 0.34, 0); rot.add(gp);
    gp.add(mesh(G.cyl, M.dark, [0.34,1.30,0.34], [0,0,0.50], [HP,0,0]));
    var gm = mesh(G.tor, GLA, [0.52,0.52,0.55], [0,0,1.16]);
    gm.userData.noMerge = 1; gp.add(gm);
    gp.visible = false; guns.push(gp);
  }
  var hub = mesh(G.oct, GL, [0.74,0.74,0.74], [0,0.40,0]);
  hub.userData.noMerge = 1; rot.add(hub);

  root.userData.rig = {hips:hips, legs:legs, rot:rot, tips:tips, guns:guns, hub:hub, GL:GL, GLA:GLA};
  return mergeBones(root);
}
export function updSichel(rig, o, dt){
  var p2 = o.p2, i, L;
  rig.sp = damp(rig.sp === undefined ? 0.6 : rig.sp, p2 ? 3.4 : 0.6 + 5.0*o.atk, 2.4, dt);
  rig.rot.rotation.y += rig.sp*dt*(1 + 2.2*o.atk);
  for (i = 0; i < rig.tips.length; i++) rig.tips[i].visible = !p2;
  for (i = 0; i < rig.guns.length; i++) rig.guns[i].visible = !!p2;
  rig.hub.scale.setScalar(1 + 0.12*Math.sin(o.t*3) + 0.55*o.tel);
  rig.GL.emissiveIntensity  = 1.9 + 2.6*o.tel + 4*o.atk*(1-o.atk);
  rig.GLA.emissiveIntensity = 2.1 + 3.0*o.tel + 5*o.atk*(1-o.atk);
  for (i = 0; i < 2; i++){
    L = rig.legs[i];
    var ph = (o.ph + i*0.5) % 1;
    var lift = Math.max(0, Math.sin(ph*Math.PI * 2))*0.44*o.mv;
    var ik = legIK(2.60 - lift, Math.cos(ph*Math.PI * 2)*0.60*o.mv);
    L.th.rotation.x = ik.hip + o.tel*0.10;
    L.sh.rotation.x = -ik.knee;
    L.ft.rotation.x = ik.knee - ik.hip - 0.10;
    L.kn.scale.setScalar(1 + 0.5*o.tel);
  }
  rig.hips.position.y = 3.10 + 0.10*Math.sin(o.ph*2*Math.PI * 2)*o.mv - 0.24*o.tel;
  rig.hips.rotation.x = 0.05*o.mv + o.tel*0.12 - o.atk*0.16;
}